import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CLUBS } from "@/data/clubs";
import SafetyMenu from "@/components/app/SafetyMenu";
import EnableNotificationsNudge from "@/components/app/EnableNotificationsNudge";

const clubColor = (n) => CLUBS.find(c => c.name === n)?.color || "#152A55";
const initialsOf = (name) => (name || "?").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();
const photoSrc = (url) => (url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

const wsUrl = (matchId) => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const wsBase = base.replace(/^http/, "ws");
  return `${wsBase}/api/ws/chat/${matchId}`;
};

export default function Chat() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [typing, setTyping] = useState(false);
  const [connected, setConnected] = useState(false);
  const [sentThisSession, setSentThisSession] = useState(false);
  const listRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimerRef = useRef(null);

  // Initial history + other info via REST
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/messages/${matchId}`);
        if (cancelled) return;
        setMessages(data.messages);
        setOther(data.other);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [matchId]);

  // WebSocket for live messages
  useEffect(() => {
    let closed = false;
    let ws;
    const connect = () => {
      ws = new WebSocket(wsUrl(matchId));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => { setConnected(false); if (!closed) setTimeout(connect, 1500); };
      ws.onerror = () => { try { ws.close(); } catch {} };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "message") {
            setMessages(m => m.some(x => x.id === msg.id) ? m : [...m, msg]);
          } else if (msg.type === "typing" && msg.sender_id !== user?.id) {
            setTyping(true);
            clearTimeout(typingTimerRef.current);
            typingTimerRef.current = setTimeout(() => setTyping(false), 2500);
          }
        } catch {}
      };
    };
    connect();
    return () => { closed = true; try { ws?.close(); } catch {} };
  }, [matchId, user?.id]);

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [messages, typing]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText("");
    setSentThisSession(true);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "message", text: t }));
    } else {
      // Fallback: REST
      try {
        const { data } = await api.post("/messages", { match_id: matchId, text: t });
        setMessages(m => [...m, data]);
      } catch { setText(t); }
    }
  };

  const onType = (v) => {
    setText(v);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: "typing" })); } catch {}
    }
  };

  return (
    <div className="min-h-screen bg-footy-bg text-white flex flex-col grain">
      <header className="sticky top-0 z-30 bg-footy-card/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-md mx-auto flex items-center gap-3 px-4 py-3">
          <Link to="/matches" data-testid="chat-back" className="p-2 -ml-2 rounded-full hover:bg-white/10"><ArrowLeft size={20} /></Link>
          {other && (
            <>
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 flex-none" style={{ borderColor: clubColor(other.club) }}>
                {other.photo_url ? <img src={photoSrc(other.photo_url)} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center font-display text-lg"
                       style={{ background: `linear-gradient(160deg, ${clubColor(other.club)}, #061022)` }}>{initialsOf(other.name)}</div>}
              </div>
              <div className="flex-1">
                <div className="font-bold leading-tight flex items-center gap-2">
                  {other.name}
                  {other.photo_verified && (
                    <span title="Verified Supporter" data-testid="chat-verified-badge"
                          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                      <ShieldCheck size={10} strokeWidth={3} /> Verified Supporter
                    </span>
                  )}
                  <span className={`inline-block w-2 h-2 rounded-full ${connected ? "bg-emerald-400" : "bg-slate-500"}`} title={connected ? "Live" : "Reconnecting…"} data-testid="ws-status" />
                </div>
                <div className="text-xs text-slate-400 uppercase tracking-wider">Supports {other.club}</div>
              </div>
              <SafetyMenu targetId={other.id} targetName={other.name} context="chat"
                          onBlocked={()=>nav("/matches", { replace: true })} variant="header" />
            </>
          )}
        </div>
      </header>

      <div ref={listRef} className="flex-1 overflow-y-auto max-w-md w-full mx-auto px-4 py-6 space-y-3" data-testid="messages-list">
        {loading ? <div className="text-center text-slate-500">Loading...</div> :
         messages.length === 0 ? (
          <div className="text-center py-16">
            <p className="font-display text-3xl uppercase text-slate-500">Kick off the chat</p>
            <p className="text-sm text-slate-500 mt-2">Say hi — talk footy, life, or your next matchday.</p>
          </div>
         ) : messages.map(m => {
          if (m.type === "system") {
            return (
              <div key={m.id} className="flex justify-center my-2" data-testid={`chat-system-${m.id}`}>
                <div className="text-[11px] text-slate-300 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-center max-w-[85%]">
                  {m.text}
                </div>
              </div>
            );
          }
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={mine
                ? "bg-footy-red text-white rounded-t-2xl rounded-bl-2xl rounded-br-md px-4 py-2.5 max-w-[80%] text-sm shadow"
                : "bg-footy-cardHover text-white rounded-t-2xl rounded-br-2xl rounded-bl-md px-4 py-2.5 max-w-[80%] text-sm border border-white/10"}>
                {m.text}
              </div>
            </div>
          );
         })}
        {typing && (
          <div className="flex justify-start" data-testid="typing-indicator">
            <div className="bg-footy-cardHover text-white rounded-t-2xl rounded-br-2xl rounded-bl-md px-4 py-2.5 text-sm border border-white/10 inline-flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "120ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "240ms" }} />
            </div>
          </div>
        )}
      </div>

      <form onSubmit={send} className="sticky bottom-0 max-w-md w-full mx-auto p-3 bg-footy-bg/95 backdrop-blur border-t border-white/10">
        {sentThisSession && (
          <div className="pb-3">
            <EnableNotificationsNudge source="chat" />
          </div>
        )}
        <div className="flex gap-2 items-center">
          <input value={text} onChange={(e)=>onType(e.target.value)}
            placeholder="Type a message..." className="footy-input flex-1 !py-3 !rounded-full"
            data-testid="chat-input" />
          <button type="submit" data-testid="chat-send"
            className="w-12 h-12 rounded-full bg-footy-red flex items-center justify-center active:scale-90 transition disabled:opacity-40"
            disabled={!text.trim()}>
            <Send size={18} />
          </button>
        </div>
      </form>
    </div>
  );
}
